import { translate } from "./translate";

/**
 * Format a timestamp as a short, human-readable relative time string,
 * e.g. "6 days ago" / "Pre 6 dana". Used by the Reengage / Winback tabs
 * so trainers can scan engagement gaps at a glance.
 *
 * Returns the translated "No data" for missing/unparseable input so the
 * UI never shows a blank or "Invalid Date".
 */
export const relativeTime = (dateString: string | null | undefined): string => {
  if (!dateString) return translate("REL_NO_DATA");

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return translate("REL_NO_DATA");

  let diffMs = Date.now() - date.getTime();
  // Future timestamps (clock skew) collapse to "Just now".
  if (diffMs < 0) diffMs = 0;

  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));

  if (days === 0) {
    const hours = Math.floor(diffMs / (60 * 60 * 1000));
    if (hours === 0) return translate("REL_JUST_NOW");
    return hours === 1
      ? translate("REL_HOUR_AGO")
      : translate("REL_HOURS_AGO").replace("{n}", String(hours));
  }

  if (days === 1) return translate("REL_YESTERDAY");
  if (days < 30) return translate("REL_DAYS_AGO").replace("{n}", String(days));

  const months = Math.floor(days / 30);
  if (months < 12) {
    return months === 1
      ? translate("REL_MONTH_AGO")
      : translate("REL_MONTHS_AGO").replace("{n}", String(months));
  }

  const years = Math.floor(days / 365);
  return years === 1
    ? translate("REL_YEAR_AGO")
    : translate("REL_YEARS_AGO").replace("{n}", String(years));
};
